/**
 * Lucky Wheel 动态数据 Hook
 * 用于 Plaza 页面的轻量级数据获取
 * 无需完整的 LuckyWheelClient 初始化
 */

import { useCallback, useEffect, useState } from "react";
import { LuckyWheelClient } from "@/lib/clients/LuckyWheelClient";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("app/plaza/hooks/useLuckyWheelDynamics.ts");


export interface DynamicMessage {
	id: string;
	type: "draw" | "verify" | "commission";
	timestamp: bigint;
	user: string;
	userName?: string;
	message: string;
	extra?: {
		prizeName?: string;
		prizeLevel?: number;
		amount?: bigint;
	};
}

export function useLuckyWheelDynamics() {
	const [messages, setMessages] = useState<DynamicMessage[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [isFirstLoad, setIsFirstLoad] = useState(true);

	// 格式化余额显示
	const formatBalance = (amount: bigint): string => {
		const num = Number(amount);
		if (num >= 10000) {
			return (num / 10000).toFixed(1) + "万";
		}
		return num.toLocaleString();
	};

	// 获取符券稀有度名称
	const getRarityName = (prizeLevel: bigint): string => {
		const level = Number(prizeLevel);
		if (level >= 81) return "传说";
		if (level >= 61) return "史诗";
		if (level >= 41) return "罕见";
		if (level >= 21) return "稀有";
		return "普通";
	};

	// 获取用户显示名称（简化版，使用 Principal ID 缩略）
	const getUserDisplayName = (userId: string): string => {
		if (userId.length > 12) {
			return `${userId.slice(0, 5)}...${userId.slice(-3)}`;
		}
		return userId;
	};

	// 获取动态数据
	const fetchDynamics = useCallback(
		async (silent = false) => {
			try {
				// 只在非静默模式下显示 loading
				if (!silent) {
					setIsLoading(true);
				}
				setError(null);

				// 使用匿名 LuckyWheelClient (不需要认证)
				const client = new LuckyWheelClient(null);

				// 获取当前轮次
				let currentRound;
				try {
					currentRound = await client.getActiveRoundInfo();
				} catch (err) {
					uiLog.error("Failed to get active round:", err);
					return;
				}

				if (!currentRound) {
					setMessages([]);
					return;
				}

				const roundId = currentRound.id;

				// 并行获取数据：特等奖 + 一等奖 + 二等奖 + 符券 + 返佣
				const [
					specialPrizeResult,
					firstPrizeResult,
					secondPrizeResult,
					verifyResult,
					commissionResult,
				] = await Promise.all([
					// 特等奖
					client.queryWinRecords(
						{ ByRound: roundId },
						null,
						{ Forward: null },
						10n,
						{ claimStatus: [], prizeLevel: [1n], minAmount: [], rarity: [] },
					),
					// 一等奖
					client.queryWinRecords(
						{ ByRound: roundId },
						null,
						{ Forward: null },
						10n,
						{ claimStatus: [], prizeLevel: [2n], minAmount: [], rarity: [] },
					),
					// 二等奖
					client.queryWinRecords(
						{ ByRound: roundId },
						null,
						{ Forward: null },
						10n,
						{ claimStatus: [], prizeLevel: [3n], minAmount: [], rarity: [] },
					),
					// 验证符券
					client.queryOperations(
						{ ByType: { VerifyTicket: null } },
						null,
						{ Forward: null },
						300n,
					),
					// 邀请返佣
					client.queryOperations(
						{ ByType: { ReferralCommission: null } },
						null,
						{ Forward: null },
						300n,
					),
				]);

				// 转换中奖记录
				const prizeMessages: DynamicMessage[] = [];

				// 处理特等奖
				for (const record of specialPrizeResult.data) {
					prizeMessages.push({
						id: `win-${record.id}`,
						type: "draw",
						timestamp: BigInt(record.winTime),
						user:
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						userName: getUserDisplayName(
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						),
						message: `投炉抽中${record.prizeName}！`,
						extra: {
							prizeName: record.prizeName,
							prizeLevel: Number(record.prizeLevel),
						},
					});
				}

				// 处理一等奖
				for (const record of firstPrizeResult.data) {
					prizeMessages.push({
						id: `win-${record.id}`,
						type: "draw",
						timestamp: BigInt(record.winTime),
						user:
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						userName: getUserDisplayName(
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						),
						message: `投炉抽中${record.prizeName}！`,
						extra: {
							prizeName: record.prizeName,
							prizeLevel: Number(record.prizeLevel),
						},
					});
				}

				// 处理二等奖
				for (const record of secondPrizeResult.data) {
					prizeMessages.push({
						id: `win-${record.id}`,
						type: "draw",
						timestamp: BigInt(record.winTime),
						user:
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						userName: getUserDisplayName(
							typeof record.userId === "string"
								? record.userId
								: record.userId.toString(),
						),
						message: `投炉抽中${record.prizeName}！`,
						extra: {
							prizeName: record.prizeName,
							prizeLevel: Number(record.prizeLevel),
						},
					});
				}

				// 筛选验证符券（prizeLevel >= 60）
				const verifyItems: DynamicMessage[] = [];
				for (const op of verifyResult.data) {
					if ("VerifyTicket" in (op as any).details) {
						const details = (op as any).details["VerifyTicket"];
						const prizeLevel = details.prizeLevel;
						if (Number(prizeLevel) >= 60) {
							const rarityName = getRarityName(prizeLevel);
							verifyItems.push({
								id: `verify-${(op as any).id}`,
								type: "verify",
								timestamp: (op as any).timestamp,
								user:
									typeof (op as any).operator === "string"
										? (op as any).operator
										: (op as any).operator.toString(),
								userName: getUserDisplayName(
									typeof (op as any).operator === "string"
										? (op as any).operator
										: (op as any).operator.toString(),
								),
								message: `观灵获得${rarityName}符券`,
								extra: { prizeLevel: Number(prizeLevel) },
							});
						}
					}
				}

				// 筛选邀请返佣（TOP10）
				const commissionItems: DynamicMessage[] = [];
				for (const op of commissionResult.data) {
					if ("ReferralCommission" in (op as any).details) {
						const details = (op as any).details["ReferralCommission"];
						const amount = details.amount;
						commissionItems.push({
							id: `commission-${(op as any).id}`,
							type: "commission",
							timestamp: (op as any).timestamp,
							user:
								typeof (op as any).operator === "string"
									? (op as any).operator
									: (op as any).operator.toString(),
							userName: getUserDisplayName(
								typeof (op as any).operator === "string"
									? (op as any).operator
									: (op as any).operator.toString(),
							),
							message: `获得邀请返佣 ${formatBalance(amount)} DD`,
							extra: { amount },
						});
					}
				}

				// 按金额降序排序，取前10
				commissionItems.sort((a, b) => {
					const amountA = a.extra?.amount || 0n;
					const amountB = b.extra?.amount || 0n;
					return Number(amountB - amountA);
				});
				const topCommissions = commissionItems.slice(0, 10);

				// 合并所有数据并按时间戳降序排序
				const allMessages = [
					...prizeMessages,
					...verifyItems,
					...topCommissions,
				];
				allMessages.sort((a, b) => Number(b.timestamp - a.timestamp));

				// 最多保留20条
				const finalMessages = allMessages.slice(0, 20);

				setMessages(finalMessages);
			} catch (err) {
				uiLog.error("Failed to fetch lucky wheel dynamics:", err);
				setError(err as Error);
			} finally {
				if (!silent) {
					setIsLoading(false);
				}
				// 标记第一次加载已完成
				if (isFirstLoad) {
					setIsFirstLoad(false);
				}
			}
		},
		[isFirstLoad],
	);

	// 初始加载
	useEffect(() => {
		fetchDynamics();
	}, [fetchDynamics]);

	// 定时刷新（每30秒，静默刷新）
	useEffect(() => {
		const timer = setInterval(() => {
			fetchDynamics(true); // 静默刷新
		}, 30000);

		return () => clearInterval(timer);
	}, [fetchDynamics]);

	return {
		messages,
		isLoading,
		error,
		refresh: fetchDynamics,
	};
}

export interface GrandPrizeInfo {
	level: number;
	prizeName: string;
	prizeId: bigint;
	baseAmount: bigint;
	dynamicAmount: bigint;
	totalAmount: bigint;
	totalStock: number;
	wonStock: number;
	remainingStock: number;
	isDynamic: boolean;
	recentWinners: {
		user: string;
		timestamp: bigint;
	}[];
}

export function useLuckyWheelGrandPrize(language: "zh" | "en") {
	const [grandPrize, setGrandPrize] = useState<GrandPrizeInfo | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const fetchGrandPrize = useCallback(async () => {
		try {
			setIsLoading(true);
			const client = new LuckyWheelClient(null);
			const prizes = await client.getAvailablePrizes();

			// 依次查找 Level 1, 2, 3
			for (let level = 1; level <= 3; level++) {
				const prize = prizes.find((p) => Number(p.level) === level);
				if (prize && Number(prize.remainingStock) > 0) {
					// 发现有库存的奖项
					const isDynamic = !!(
						prize.dynamicPool &&
						prize.dynamicPool.length > 0 &&
						prize.dynamicPool[0]?.enabled
					);
					const dynamicPool = isDynamic ? prize.dynamicPool?.[0] : null;

					const baseAmount = dynamicPool
						? dynamicPool.baseAmount
						: "Balance" in prize.prizeType
							? prize.prizeType.Balance
							: 0n;
					const dynamicAmount =
						dynamicPool && Number(prize.remainingStock) > 0
							? dynamicPool.accumulatedAmount / prize.remainingStock
							: 0n;

					// 获取该奖项的最近中奖者
					const winnersResult = await client.queryWinRecords(
						{ Global: null },
						null,
						{ Forward: null },
						5n,
						{
							claimStatus: [],
							prizeLevel: [BigInt(level)],
							minAmount: [],
							rarity: [],
						},
					);

					setGrandPrize({
						level,
						prizeName: prize.name,
						prizeId: BigInt(prize.id),
						baseAmount,
						dynamicAmount,
						totalAmount: baseAmount + dynamicAmount,
						totalStock: Number(prize.totalStock),
						remainingStock: Number(prize.remainingStock),
						wonStock: Number(prize.totalStock) - Number(prize.remainingStock),
						isDynamic: !!isDynamic,
						recentWinners: winnersResult.data.map((r) => ({
							user: typeof r.userId === "string" ? r.userId : r.userId.toString(),
							timestamp: BigInt(r.winTime),
						})),
					});
					setIsLoading(false);
					return;
				}
			}

			// 如果都没有
			setGrandPrize(null);
		} catch (err) {
			uiLog.error("Failed to fetch grand prize info:", err);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchGrandPrize();
	}, [fetchGrandPrize]);

	return { grandPrize, isLoading, refresh: fetchGrandPrize };
}
